import type { CanFrame } from '../canlog/types';
import { extractSignalValue, type SignalExtractionConfig } from './bitExtraction';

const MAX_CORRELATION_IDS = 20;
const MAX_SAMPLES_PER_CANDIDATE = 2200;
const LAG_STEPS_MS = [-200, -160, -120, -80, -40, 0, 40, 80, 120, 160, 200];

export type PidSample = {
  timestampMs: number;
  pidName: string;
  value: number;
};

export type PidSeries = {
  pidName: string;
  points: Array<{ t: number; value: number }>;
};

export type PidCorrelationCandidate = {
  id: string;
  pidName: string;
  canId: number;
  canIdHex: string;
  startBit: number;
  length: number;
  endianness: 'little' | 'big';
  byteLabel: string;
  correlation: number;
  signedCorrelation: number;
  bestLagMs: number;
  sampleCount: number;
  slope: number;
  offset: number;
  direction: 'same' | 'inverse';
  entropyNormalized: number;
  changeRatio: number;
  rawPoints: Array<{ t: number; value: number }>;
  pidPoints: Array<{ t: number; value: number }>;
};

function formatCanId(canId: number) {
  return `0x${canId.toString(16).toUpperCase().padStart(canId > 0x7ff ? 8 : 3, '0')}`;
}

function normalizePidName(name: string) {
  return name.trim().replace(/\s+/g, ' ') || 'PID';
}

function downsample<T>(items: T[], maxItems = MAX_SAMPLES_PER_CANDIDATE) {
  if (items.length <= maxItems) return items;
  const step = Math.ceil(items.length / maxItems);
  return items.filter((_, index) => index % step === 0).slice(0, maxItems);
}

function mean(values: number[]) {
  return values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
}

function variance(values: number[], avg = mean(values)) {
  if (values.length < 2) return 0;
  return values.reduce((sum, value) => sum + (value - avg) ** 2, 0) / (values.length - 1);
}

function pearson(xValues: number[], yValues: number[]) {
  if (xValues.length < 4 || xValues.length !== yValues.length) return 0;
  const xMean = mean(xValues);
  const yMean = mean(yValues);
  let numerator = 0;
  let xDenominator = 0;
  let yDenominator = 0;
  for (let index = 0; index < xValues.length; index += 1) {
    const xDelta = xValues[index] - xMean;
    const yDelta = yValues[index] - yMean;
    numerator += xDelta * yDelta;
    xDenominator += xDelta ** 2;
    yDenominator += yDelta ** 2;
  }
  const denominator = Math.sqrt(xDenominator * yDenominator);
  return denominator === 0 ? 0 : numerator / denominator;
}

function valueEntropyNormalized(values: number[]) {
  if (values.length === 0) return 0;
  const counts = new Map<number, number>();
  for (const value of values) counts.set(Math.round(value), (counts.get(Math.round(value)) ?? 0) + 1);
  const entropy = Array.from(counts.values()).reduce((sum, count) => {
    const p = count / values.length;
    return sum - p * Math.log2(p);
  }, 0);
  return Math.min(1, entropy / 8);
}

function valueChangeRatio(values: number[]) {
  if (values.length < 2) return 0;
  let changes = 0;
  for (let index = 1; index < values.length; index += 1) {
    if (values[index] !== values[index - 1]) changes += 1;
  }
  return changes / (values.length - 1);
}

function regression(rawValues: number[], pidValues: number[]) {
  const rawMean = mean(rawValues);
  const pidMean = mean(pidValues);
  const rawVariance = variance(rawValues, rawMean);
  if (rawVariance === 0) return { slope: 0, offset: pidMean };
  let covariance = 0;
  for (let index = 0; index < rawValues.length; index += 1) {
    covariance += (rawValues[index] - rawMean) * (pidValues[index] - pidMean);
  }
  covariance /= Math.max(1, rawValues.length - 1);
  const slope = covariance / rawVariance;
  return {
    slope,
    offset: pidMean - slope * rawMean
  };
}

function interpolate(points: Array<{ t: number; value: number }>, timestampMs: number) {
  if (points.length === 0) return null;
  if (timestampMs < points[0].t || timestampMs > points[points.length - 1].t) return null;

  let low = 0;
  let high = points.length - 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (points[mid].t === timestampMs) return points[mid].value;
    if (points[mid].t < timestampMs) low = mid + 1;
    else high = mid - 1;
  }

  const previous = points[Math.max(0, high)];
  const next = points[Math.min(points.length - 1, low)];
  if (!previous || !next) return null;
  if (previous.t === next.t) return previous.value;
  const ratio = (timestampMs - previous.t) / (next.t - previous.t);
  return previous.value + (next.value - previous.value) * ratio;
}

function groupPidSamples(samples: PidSample[]): PidSeries[] {
  const groups = new Map<string, Array<{ t: number; value: number }>>();
  for (const sample of samples) {
    if (!Number.isFinite(sample.timestampMs) || !Number.isFinite(sample.value)) continue;
    const pidName = normalizePidName(sample.pidName);
    groups.set(pidName, [...(groups.get(pidName) ?? []), { t: sample.timestampMs, value: sample.value }]);
  }
  return Array.from(groups.entries()).map(([pidName, points]) => ({
    pidName,
    points: points.sort((a, b) => a.t - b.t)
  })).filter((series) => series.points.length >= 2);
}

function parseCsv(text: string): PidSample[] {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((header) => header.trim().toLowerCase());
  const timestampIndex = headers.findIndex((header) => ['timestamp_ms', 'timestamp', 'time_ms', 't'].includes(header));
  const nameIndex = headers.findIndex((header) => ['pid_name', 'pid', 'name', 'signal'].includes(header));
  const valueIndex = headers.findIndex((header) => ['value', 'val'].includes(header));
  if (timestampIndex < 0 || nameIndex < 0 || valueIndex < 0) return [];

  return lines.slice(1).flatMap((line) => {
    const columns = line.split(',').map((column) => column.trim());
    const timestampMs = Number.parseFloat(columns[timestampIndex]);
    const value = Number.parseFloat(columns[valueIndex]);
    const pidName = columns[nameIndex];
    return Number.isFinite(timestampMs) && Number.isFinite(value) && pidName
      ? [{ timestampMs, value, pidName }]
      : [];
  });
}

export function parsePidOverlayText(text: string): PidSeries[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    const payload = JSON.parse(trimmed) as unknown;
    const records = Array.isArray(payload)
      ? payload
      : typeof payload === 'object' && payload !== null && 'points' in payload && Array.isArray((payload as { points: unknown }).points)
        ? (payload as { points: unknown[] }).points
        : [];
    const samples = records.flatMap((record) => {
      if (!record || typeof record !== 'object') return [];
      const item = record as Record<string, unknown>;
      const timestampMs = Number(item.timestamp_ms ?? item.timestampMs ?? item.timestamp ?? item.t);
      const value = Number(item.value);
      const pidName = String(item.pid_name ?? item.pidName ?? item.pid ?? item.name ?? 'PID');
      return Number.isFinite(timestampMs) && Number.isFinite(value)
        ? [{ timestampMs, value, pidName }]
        : [];
    });
    return groupPidSamples(samples);
  }
  return groupPidSamples(parseCsv(trimmed));
}

export function buildDemoPidSeries(frames: CanFrame[]): PidSeries[] {
  const validTimestamps = frames.map((frame) => frame.timestampMs).filter(Number.isFinite);
  const start = validTimestamps.length > 0 ? Math.min(...validTimestamps) : 0;
  const end = validTimestamps.length > 0 ? Math.max(...validTimestamps) : 1000;
  const span = Math.max(600, end - start);
  const count = 80;
  const makeSeries = (pidName: string, fn: (ratio: number) => number): PidSeries => ({
    pidName,
    points: Array.from({ length: count }, (_, index) => {
      const ratio = index / (count - 1);
      return { t: start + ratio * span, value: fn(ratio) };
    })
  });

  return [
    makeSeries('RPM', (ratio) => 750 + Math.sin(ratio * Math.PI * 2.2) * 420 + Math.max(0, Math.sin((ratio - 0.2) * Math.PI * 3)) * 900),
    makeSeries('Vehicle Speed', (ratio) => Math.max(0, 18 + Math.sin(ratio * Math.PI * 1.2) * 14 + ratio * 22)),
    makeSeries('Throttle Position', (ratio) => Math.max(0, 18 + Math.sin(ratio * Math.PI * 4) * 16)),
    makeSeries('Engine Load', (ratio) => Math.max(5, 36 + Math.sin(ratio * Math.PI * 3.2) * 26))
  ];
}

function canIdActivity(frames: CanFrame[]) {
  const groups = new Map<number, { canId: number; canIdHex: string; frames: CanFrame[]; changes: number }>();
  for (const frame of frames) {
    let group = groups.get(frame.canId);
    if (!group) {
      group = { canId: frame.canId, canIdHex: frame.canIdHex || formatCanId(frame.canId), frames: [], changes: 0 };
      groups.set(frame.canId, group);
    }
    const previous = group.frames.at(-1);
    if (previous && previous.dataHex !== frame.dataHex) group.changes += 1;
    group.frames.push(frame);
  }
  return Array.from(groups.values())
    .sort((a, b) => (b.changes + b.frames.length * 0.05) - (a.changes + a.frames.length * 0.05))
    .slice(0, MAX_CORRELATION_IDS);
}

function candidateConfigs() {
  const configs: Array<{ startBit: number; length: number; endianness: 'little' | 'big'; byteLabel: string }> = [];
  for (let byteIndex = 0; byteIndex < 8; byteIndex += 1) {
    configs.push({ startBit: byteIndex * 8, length: 8, endianness: 'little', byteLabel: `B${byteIndex}` });
  }
  for (let byteIndex = 0; byteIndex < 7; byteIndex += 1) {
    configs.push({ startBit: byteIndex * 8, length: 16, endianness: 'little', byteLabel: `B${byteIndex}-B${byteIndex + 1} LE` });
    configs.push({ startBit: byteIndex * 8, length: 16, endianness: 'big', byteLabel: `B${byteIndex}-B${byteIndex + 1} BE` });
  }
  return configs;
}

function looksLikeCounter(values: number[], length: number) {
  if (length > 8 || values.length < 10) return false;
  let sequential = 0;
  let wraps = 0;
  const modulo = 2 ** Math.min(length, 8);
  for (let index = 1; index < values.length; index += 1) {
    const previous = Math.trunc(values[index - 1]);
    const current = Math.trunc(values[index]);
    if (current === (previous + 1) % modulo) sequential += 1;
    if (current < previous) wraps += 1;
  }
  return sequential / (values.length - 1) > 0.72 || wraps > 2;
}

function correlateCandidate(pid: PidSeries, frames: CanFrame[], config: SignalExtractionConfig) {
  const sampledFrames = downsample(frames);
  const rawPoints = sampledFrames.flatMap((frame) => {
    const value = extractSignalValue(frame.data, config);
    return value === null || !Number.isFinite(value) || !Number.isFinite(frame.timestampMs)
      ? []
      : [{ t: frame.timestampMs, value }];
  });
  if (rawPoints.length < 8) return null;
  const rawValues = rawPoints.map((point) => point.value);
  if (new Set(rawValues.map((value) => Number(value.toFixed(4)))).size < 3) return null;
  if (looksLikeCounter(rawValues, config.length)) return null;

  let best = { signedCorrelation: 0, bestLagMs: 0, pidValues: [] as number[], rawValues: [] as number[] };
  for (const lagMs of LAG_STEPS_MS) {
    const pairs = rawPoints.flatMap((point) => {
      const pidValue = interpolate(pid.points, point.t + lagMs);
      return pidValue === null ? [] : [{ raw: point.value, pid: pidValue }];
    });
    if (pairs.length < 8) continue;
    const nextRaw = pairs.map((pair) => pair.raw);
    const nextPid = pairs.map((pair) => pair.pid);
    const signedCorrelation = pearson(nextRaw, nextPid);
    if (Math.abs(signedCorrelation) > Math.abs(best.signedCorrelation)) {
      best = { signedCorrelation, bestLagMs: lagMs, rawValues: nextRaw, pidValues: nextPid };
    }
  }

  if (best.rawValues.length < 8) return null;
  const fit = regression(best.rawValues, best.pidValues);
  const pidPoints = rawPoints.flatMap((point) => {
    const pidValue = interpolate(pid.points, point.t + best.bestLagMs);
    return pidValue === null ? [] : [{ t: point.t, value: pidValue }];
  });

  return {
    rawPoints,
    pidPoints,
    signedCorrelation: best.signedCorrelation,
    correlation: Math.abs(best.signedCorrelation),
    bestLagMs: best.bestLagMs,
    sampleCount: best.rawValues.length,
    slope: fit.slope,
    offset: fit.offset,
    entropyNormalized: valueEntropyNormalized(rawValues),
    changeRatio: valueChangeRatio(rawValues)
  };
}

export function runPidCorrelation(frames: CanFrame[], pid: PidSeries): PidCorrelationCandidate[] {
  const results: PidCorrelationCandidate[] = [];
  for (const group of canIdActivity(frames)) {
    for (const configInfo of candidateConfigs()) {
      const config: SignalExtractionConfig = {
        startBit: configInfo.startBit,
        length: configInfo.length,
        endianness: configInfo.endianness,
        signed: false,
        factor: 1,
        offset: 0
      };
      const result = correlateCandidate(pid, group.frames, config);
      if (!result || result.correlation < 0.45) continue;
      results.push({
        id: `${pid.pidName}-${group.canId}-${configInfo.byteLabel}`,
        pidName: pid.pidName,
        canId: group.canId,
        canIdHex: group.canIdHex,
        startBit: configInfo.startBit,
        length: configInfo.length,
        endianness: configInfo.endianness,
        byteLabel: configInfo.byteLabel,
        correlation: result.correlation,
        signedCorrelation: result.signedCorrelation,
        bestLagMs: result.bestLagMs,
        sampleCount: result.sampleCount,
        slope: result.slope,
        offset: result.offset,
        direction: result.signedCorrelation >= 0 ? 'same' : 'inverse',
        entropyNormalized: result.entropyNormalized,
        changeRatio: result.changeRatio,
        rawPoints: result.rawPoints,
        pidPoints: result.pidPoints
      });
    }
  }
  return results.sort((a, b) => b.correlation - a.correlation || b.sampleCount - a.sampleCount).slice(0, 40);
}
